/**
 * Bunny.net File Picker Component
 * Ermöglicht die Auswahl von Dateien aus Bunny.net CDN
 */

import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  PhotoIcon,
  MusicalNoteIcon,
  FilmIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  FolderIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { bunnyCDNService, BunnyFile } from '../../services/bunnyCDNService';

interface BunnyFilePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
  fileType?: 'image' | 'audio' | 'video' | 'all';
  allowUrlInput?: boolean;
  title?: string;
}

export const BunnyFilePicker: React.FC<BunnyFilePickerProps> = ({
  isOpen,
  onClose,
  onSelect,
  fileType = 'all',
  allowUrlInput = true,
  title = 'Datei von Bunny.net auswählen',
}) => {
  const [files, setFiles] = useState<BunnyFile[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  const [currentFolder, setCurrentFolder] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [manualUrl, setManualUrl] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadFiles();
      loadFolders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentFolder, fileType]);

  const loadFiles = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await bunnyCDNService.listFiles(currentFolder);
      
      if (result.success && result.data) {
        // Filtere Dateien nach Typ
        let filteredFiles = result.data;
        
        if (fileType === 'image') {
          filteredFiles = result.data.filter(file => 
            file.contentType.startsWith('image/')
          );
        } else if (fileType === 'audio') {
          filteredFiles = result.data.filter(file => 
            file.contentType.startsWith('audio/')
          );
        } else if (fileType === 'video') {
          filteredFiles = result.data.filter(file => 
            file.contentType.startsWith('video/')
          );
        }
        
        setFiles(filteredFiles);
      } else {
        setError(result.message || 'Fehler beim Laden der Dateien');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unbekannter Fehler');
    } finally {
      setIsLoading(false);
    }
  };

  const loadFolders = async () => {
    try {
      const result = await bunnyCDNService.listFolders(currentFolder);
      if (result.success && result.data) {
        setFolders(result.data);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Ordner:', error);
    }
  };

  const handleFileSelect = (file: BunnyFile) => {
    onSelect(file.url);
    onClose();
  };

  const handleFolderClick = (folder: string) => {
    const newPath = currentFolder ? `${currentFolder}/${folder}` : folder;
    setCurrentFolder(newPath);
    setSearchQuery('');
  };

  const handleBackClick = () => {
    const pathParts = currentFolder.split('/').filter(Boolean);
    pathParts.pop();
    setCurrentFolder(pathParts.join('/'));
    setSearchQuery('');
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      // Optional: Zeige eine Toast-Nachricht
    } catch (error) {
      console.error('Fehler beim Kopieren:', error);
    }
  };

  const handleManualUrlSubmit = () => {
    if (manualUrl.trim()) {
      onSelect(manualUrl.trim());
      onClose();
    }
  };

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('audio/')) return <MusicalNoteIcon className="w-6 h-6 text-blue-500" />;
    if (contentType.startsWith('video/')) return <FilmIcon className="w-6 h-6 text-purple-500" />;
    if (contentType.startsWith('image/')) return <PhotoIcon className="w-6 h-6 text-green-500" />;
    return <DocumentTextIcon className="w-6 h-6 text-gray-500" />;
  };

  const filteredFiles = files.filter(file =>
    file.path.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div 
      className="bunny-file-picker-overlay"
      onClick={(e) => {
        // Schließe beim Klick auf den Overlay-Hintergrund
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold text-secondary-900">{title}</h2>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            className="text-secondary-500 hover:text-secondary-700"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-4 text-sm">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCurrentFolder('');
              }}
              className="text-primary-600 hover:text-primary-700"
              disabled={!currentFolder}
            >
              Root
            </button>
            {currentFolder.split('/').filter(Boolean).map((folder, index, arr) => {
              const path = arr.slice(0, index + 1).join('/');
              return (
                <React.Fragment key={path}>
                  <span className="text-secondary-400">/</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCurrentFolder(path);
                    }}
                    className="text-primary-600 hover:text-primary-700"
                  >
                    {folder}
                  </button>
                </React.Fragment>
              );
            })}
            {currentFolder && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleBackClick();
                }}
                className="ml-2 text-secondary-500 hover:text-secondary-700 text-xs"
              >
                ← Zurück
              </button>
            )}
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary-400" />
            <input
              type="text"
              placeholder="Dateien durchsuchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-secondary-600">Lade Dateien...</p>
            </div>
          )}

          {/* Folders */}
          {!isLoading && folders.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-secondary-700 mb-2">Ordner</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {folders.map((folder) => (
                  <button
                    key={folder}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleFolderClick(folder);
                    }}
                    className="flex items-center gap-2 p-2 border border-secondary-200 rounded-lg hover:bg-secondary-50 text-left"
                  >
                    <FolderIcon className="w-5 h-5 text-orange-500" />
                    <span className="text-sm text-secondary-700 truncate">{folder}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Files */}
          {!isLoading && (
            <div>
              <h3 className="text-sm font-medium text-secondary-700 mb-2">
                Dateien {filteredFiles.length > 0 && `(${filteredFiles.length})`}
              </h3>
              {filteredFiles.length === 0 ? (
                <div className="text-center py-8 text-secondary-500">
                  <p>Keine Dateien gefunden</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {filteredFiles.map((file) => (
                    <div
                      key={file.path}
                      className="flex items-center gap-3 p-3 border border-secondary-200 rounded-lg hover:bg-secondary-50 cursor-pointer group"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleFileSelect(file);
                      }}
                    >
                      {getFileIcon(file.contentType)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-secondary-900 truncate">
                          {file.path.split('/').pop()}
                        </p>
                        <p className="text-xs text-secondary-500 truncate">
                          {file.url.substring(0, 50)}...
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCopyUrl(file.url);
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-secondary-500 hover:text-primary-600"
                        title="URL kopieren"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Manual URL Input */}
          {allowUrlInput && (
            <div className="mt-6 pt-6 border-t">
              <h3 className="text-sm font-medium text-secondary-700 mb-2">
                Oder URL manuell eingeben
              </h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualUrl}
                  onChange={(e) => setManualUrl(e.target.value)}
                  placeholder="https://ul-app.b-cdn.net/..."
                  className="flex-1 px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleManualUrlSubmit()}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleManualUrlSubmit();
                  }}
                  className="btn btn-primary"
                  disabled={!manualUrl.trim()}
                >
                  Verwenden
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            className="btn btn-outline"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
};

